import { RegisterScreen } from '@/components/RegisterScreen';
import type { FormValues } from '@/components/ChildRecords';
import { StatusPill, formatDate, formatNumber } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import { HARVEST_CONDITIONS } from '@/lib/harvestConditions';
import { FSC_CLAIMS } from '@/lib/practiceLists';
import { ActivityLabour } from '@/screens/parts/ActivityLabour';
import { doneByFields, usePracticePickers } from '@/screens/parts/practiceFields';
import {
  HarvestPhotos, HarvestRestocking, HarvestTrucks, REGENERATION_STATES, regenerationFields, yieldFields,
} from '@/screens/parts/HarvestParts';

interface Row {
  id: string; harvest_code: string | null; plot_id: string; plot_code: string | null; farmer_id: string; farmer_name: string | null;
  farmer_code: string | null; species_label: string | null; harvest_date: string; trees_harvested: number; status: string;
  permit_no: string | null; internal_permit_id: string | null; internal_permit_no: string | null; internal_permit_code: string | null;
  internal_permit_conditions: string[] | null; felling_started_on: string | null; felling_completed_on: string | null;
  felled_by: string | null; conditions_met: string[] | null; incidents: string | null; evidence_state: string;
  report_document_id: string | null;
  done_by_type: string | null; done_by_contractor_id: string | null; done_by_display: string | null;
  yield_basis: string | null; yield_volume_m3: number | null; yield_weight_t: number | null; yield_log_count: number | null;
  regeneration_method: string | null; regeneration_saplings_planned: number | null; regeneration_due_on: string | null; regeneration_state: string;
  site_photos: number; log_photos: number; photos_complete: boolean;
}

export const yieldText = (row: { yield_basis: string | null; yield_volume_m3: number | null; yield_weight_t: number | null; yield_log_count: number | null }) => (
  row.yield_basis === 'log_volume' && row.yield_volume_m3 != null ? `${formatNumber(row.yield_volume_m3, 3)} m³ · ${formatNumber(row.yield_log_count, 0)} logs`
    : row.yield_basis === 'weight' && row.yield_weight_t != null ? `${formatNumber(row.yield_weight_t, 3)} t` : null);

/**
 * The internal harvest report (owner, 2026-09-27: "Harvest Report (Internal)"): for each harvest,
 * what happened on the ground - when, by whom, which of the internal permit's conditions were met,
 * any incident, what became of the residue - against the permit it was felled under.
 *
 * A harvest is RECORDED on Tree inventory, where the stock and permit checks run; this register
 * reports on it, so it adds nothing of its own. The conditions offered are the internal permit's.
 */
export default function HarvestReportScreen() {
  const pick = usePracticePickers();
  const internalPermits = useRows<{ id: string; farmer_id: string; plot_id: string | null; permit_no: string | null; permit_code: string | null;
                                    effective_status: string; conditions: string[] }>(
    'harvest_permits', { filters: { permit_class: 'internal' } });
  const permitOf = (form: FormValues) => (internalPermits.data ?? []).find((row) => row.id === form.internal_permit_id);
  // The FD permit and the FSC claim can be named after the harvest was recorded (found in the
  // 2026-09-28 UI pass: a harvest recorded before its plot's scope could never be claimed).
  const fdPermits = useRows<{ id: string; farmer_id: string; farmer_name: string | null; permit_no: string | null; permit_code: string | null; effective_status: string; permit_type: string }>(
    'harvest_permits', { filters: { permit_class: 'government' } });
  const unmet = (row: Row) => (row.internal_permit_conditions ?? []).filter((id) => !(row.conditions_met ?? []).includes(id)).length;

  return (
    <RegisterScreen<Row>
      plotFilter canAdd={false}
      // The harvest package (owner, 2026-09-27): photos, the crew's pay, the trucks and the restocking.
      moreTabs={(row) => [
        { label: 'Photos', content: <HarvestPhotos harvest={row} /> },
        { label: 'Labour', content: <ActivityLabour activity={{ id: row.id, plot_id: row.plot_id, started_on: row.felling_started_on ?? row.harvest_date,
            completed_on: row.felling_completed_on, done_by_type: row.done_by_type, done_by_contractor_id: row.done_by_contractor_id,
            done_by_contractor_name: row.done_by_type === 'contractor' ? row.done_by_display : null }} link="harvest_id" /> },
        { label: 'Trucks', content: <HarvestTrucks harvestId={row.id} /> },
        { label: 'Restocking', content: <HarvestRestocking harvest={row} /> },
      ]}
      addLabel="" exportName="harvest-report" recordNoun="harvest"
      orderBy={{ column: 'harvest_date', ascending: false }}
      recordLabel={(row) => `harvest report ${row.harvest_code ?? ''}`}
      screenKey="plot.harvest"
      searchColumns={['harvest_code', 'plot_code', 'farmer_name', 'farmer_code', 'species_label', 'permit_no', 'internal_permit_no']}
      secondary={{ sum: (row) => row.trees_harvested, unit: 'trees', digits: 0 }}
      subtitle="What happened on each harvest: when, by whom, the permit's conditions, the yield, the photos, the trucks and how the site regenerates. Harvests are recorded on Tree inventory."
      table="ops.plot_harvest_entries" title="Harvest report" view="plot_harvest_entries"
      columns={[
        { key: 'harvest_code', label: 'Harvest', render: (row) => (
          <span className="cell-stack"><strong>{row.harvest_code ?? '--'}</strong><small className="muted">{formatDate(row.harvest_date)}</small></span>
        ) },
        { key: 'farmer_name', label: 'Farmer', render: (row) => (
          <span className="cell-stack"><strong>{row.farmer_name ?? '--'}</strong>
            <small className="muted">{[row.farmer_code, row.plot_code].filter(Boolean).join(' · ')}</small></span>
        ) },
        { key: 'species_label', label: 'Species', render: (row) => row.species_label ?? '--' },
        { key: 'trees_harvested', label: 'Trees', align: 'right', render: (row) => formatNumber(row.trees_harvested, 0) },
        { key: 'internal_permit_no', label: 'Internal permit', render: (row) => row.internal_permit_no ?? row.internal_permit_code
          ?? <span className="field-error">none</span> },
        { key: 'permit_no', label: 'FD permit', render: (row) => row.permit_no ?? '--' },
        { key: 'felling_completed_on', label: 'Felled', render: (row) => (row.felling_completed_on ? formatDate(row.felling_completed_on) : '--') },
        { key: 'conditions_met', label: 'Conditions met', render: (row) => {
          const asked = (row.internal_permit_conditions ?? []).length;
          if (!asked) return '--';
          const missing = unmet(row);
          return missing ? <span className="field-error">{asked - missing} of {asked}</span> : `${asked} of ${asked}`;
        } },
        { key: 'yield_volume_m3', label: 'Yield', align: 'right', render: (row) => yieldText(row) ?? '--' },
        { key: 'photos_complete', label: 'Photos', render: (row) => (row.photos_complete ? '4 of 4'
          : <span className="field-error">{Math.min(row.site_photos, 2) + Math.min(row.log_photos, 2)} of 4</span>) },
        { key: 'regeneration_state', label: 'Regeneration', render: (row) => <StatusPill value={REGENERATION_STATES[row.regeneration_state] ?? row.regeneration_state} /> },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      stats={[
        { key: 'no_permit', label: 'No internal permit', tone: 'error', match: (row) => !row.internal_permit_id },
        { key: 'unreported', label: 'Not reported yet', tone: 'warn', match: (row) => !row.felling_completed_on },
        { key: 'unmet', label: 'Condition not met', tone: 'error', match: (row) => unmet(row) > 0 },
        { key: 'incident', label: 'Incident recorded', tone: 'warn', match: (row) => Boolean(row.incidents) },
        { key: 'photos', label: 'Photos short', tone: 'warn', match: (row) => !row.photos_complete },
        { key: 'no_yield', label: 'No yield statement', tone: 'warn', match: (row) => !yieldText(row) },
        { key: 'regen', label: 'Restocking due / overdue', tone: 'error', match: (row) => ['pending', 'overdue', 'not_set'].includes(row.regeneration_state) },
      ]}
      fields={[
        { key: 'internal_permit_id', label: 'Internal permit', type: 'select',
          options: (internalPermits.data ?? []).filter((row) => row.effective_status === 'granted')
            .map((row) => ({ id: row.id, label: row.permit_no ?? row.permit_code ?? 'Permit' })),
          placeholder: 'Select the internal permit', hint: 'Only approved permits in force are listed; the harvest must fall inside it.' },
        { key: 'permit_id', label: 'FD permit', type: 'select',
          options: (fdPermits.data ?? []).filter((row) => row.effective_status === 'granted' || row.permit_type === 'exempt_registration')
            .map((row) => ({ id: row.id, label: `${row.permit_no ?? row.permit_code ?? 'Permit'} - ${row.farmer_name ?? ''}` })),
          placeholder: 'None needed, or not granted yet', hint: 'Granted Forest Department permits; it must be this farmer\'s, for this plot.' },
        { key: 'fsc_claim', label: 'FSC claim', type: 'select', options: FSC_CLAIMS,
          hint: 'No more than the plot\'s own claim; a sale from this harvest can claim no more than this.' },
        { key: 'felling_started_on', label: 'Felling started', type: 'date' },
        { key: 'felling_completed_on', label: 'Felling completed', type: 'date' },
        ...doneByFields('done_by', pick.workers, pick.contractors).map((field, at) => (at === 0
          ? { ...field, label: 'Felled by', required: false, hint: 'The crew\'s workers and their pay go on the Labour tab.' } : field)),
        { key: 'conditions_met', label: 'Conditions met', type: 'multi', toggles: true, section: 'Conditions',
          optionsFor: (form) => {
            const asked = permitOf(form)?.conditions ?? [];
            return HARVEST_CONDITIONS.filter((condition) => asked.length === 0 || asked.includes(condition.id));
          },
          hint: 'The internal permit\'s conditions. Switch on each one that was met on the ground.' },
        { key: 'incidents', label: 'Incidents', type: 'textarea', section: 'Report', placeholder: 'Injury, damage to a buffer or a neighbour\'s trees, dispute ... or leave blank' },
        { key: 'residue_management', label: 'Residue', type: 'text', placeholder: 'e.g. lopped and left to decompose; branches taken as fuelwood' },
        { key: 'report_remarks', label: 'Remarks', type: 'textarea' },
        { key: 'report_document_id', label: 'Signed report', type: 'file', hint: 'Site and log photographs go on the Photos tab: two of each.' },
        ...yieldFields(),
        ...regenerationFields(),
      ]}
      validate={(form) => ({
        felling_completed_on: form.felling_started_on && form.felling_completed_on && String(form.felling_completed_on) < String(form.felling_started_on)
          ? 'Felling cannot finish before it started.' : undefined,
      })}
    />
  );
}
